// Constants
// Actions
import { CancelTrackedInteractionAction, CompleteTrackedInteractionAction, StartTrackedInteractionAction, ViewRenderAction } from "@ms/planner-actions/lib/local/performanceCollection/PerformanceCollectionAction";
import { MarkerTag } from "@ms/uno-telemetry/lib/local/performance/constants/PerformanceMarkerConstants";
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
// Performance
import { namesNoWork, TrackedInteractions } from "@ms/planner-stores/lib/local/performance/InteractionRegistry";
export class PerformanceCollectionModule {
    setViewRenderState(viewId, state, extraData) {
        this.dispatcher.dispatchActionAsync(new ViewRenderAction(viewId, state, extraData));
    }
    completeInteractions(targetViewId, renderState) {
        if (!this.configProvider().settings.performanceSettings.enableLatencyTracking) {
            return;
        }
        const perfStore = this.storeLoader.load(Store.PerformanceCollection);
        for (const pending of perfStore.getPendingInteractionsByViewId(targetViewId)){
            // The action layer sits below the registry, so the store keeps name and key apart. They were
            // written together by `startInteraction`, so recomposing them here is always the declared pair.
            const interactionId = {
                name: pending.name,
                key: pending.uniqueKey
            };
            if (TrackedInteractions[pending.name].isComplete(interactionId, this.storeLoader, pending.targetViewId, renderState)) {
                this.settleTrackedInteraction(targetViewId, pending);
            }
        }
    }
    cancelInteractions(targetViewId) {
        if (!this.configProvider().settings.performanceSettings.enableLatencyTracking) {
            return;
        }
        const perfStore = this.storeLoader.load(Store.PerformanceCollection);
        this.cancelTrackedInteractions(targetViewId, perfStore.getPendingInteractionsByViewId(targetViewId));
    }
    startInteraction(interactionId, targetViewId, entryPoint, startTime, extraData) {
        if (!this.configProvider().settings.performanceSettings.enableLatencyTracking) {
            return;
        }
        const interactionStartTime = startTime ?? performance.now();
        // Nothing was asked for, so there is nothing to measure and nothing to prove it finished.
        if (namesNoWork(interactionId.key)) {
            return;
        }
        // Already true before anything was dispatched, so no repaint is owed and only unrelated work
        // later could settle it.
        if (TrackedInteractions[interactionId.name].isComplete(interactionId, this.storeLoader, targetViewId, null)) {
            return;
        }
        const perfStore = this.storeLoader.load(Store.PerformanceCollection);
        // The older one is closed out with its own start time, so its measurement reflects the wait the
        // user actually had.
        const superseded = perfStore.getPendingInteraction(targetViewId, interactionId.name);
        if (superseded != null) {
            this.abandonTrackedInteraction(superseded);
        }
        this.startTrackedInteraction(interactionId, interactionStartTime, targetViewId, entryPoint, extraData);
    }
    startTrackedInteraction(interactionId, startTime, targetViewId, entryPoint, extraData) {
        this.dispatcher.dispatchActionAsync(new StartTrackedInteractionAction(interactionId.name, startTime, targetViewId, interactionId.key, entryPoint, extraData));
    }
    settleTrackedInteraction(targetViewId, interaction) {
        this.logInteraction(interaction, "settled");
        this.dispatcher.dispatchActionAsync(new CompleteTrackedInteractionAction(targetViewId, interaction.name));
    }
    abandonTrackedInteraction(interaction) {
        this.logInteraction(interaction, "abandoned");
    }
    cancelTrackedInteractions(targetViewId, pending) {
        for (const interaction of pending){
            this.logInteraction(interaction, "abandoned");
        }
        this.dispatcher.dispatchActionAsync(new CancelTrackedInteractionAction(targetViewId));
    }
    /**
     * Emit one measurement. Private so a duration can only be recorded as part of ending an interaction,
     * never for one that is still in flight.
     */ logInteraction(interaction, endMode) {
        const duration = performance.now() - interaction.startTime;
        // Log as performance mark (flows through existing telemetry pipeline)
        // Target thresholds (metTarget) are computed in Kusto, not client-side
        this.performanceMarker.mark(MarkerTag.UserInteraction, {
            ...interaction.extraData,
            entryPoint: interaction.entryPoint,
            name: interaction.name,
            duration: String(Math.round(duration)),
            endMode
        });
    }
    constructor(dispatcher, performanceMarker, storeLoader, configProvider){
        this.dispatcher = dispatcher;
        this.performanceMarker = performanceMarker;
        this.storeLoader = storeLoader;
        this.configProvider = configProvider;
    }
}
